import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import type { ConversationItem } from '@vivport/backend/types';
import { sendMessageDraft } from '@/api/content-api';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { contentKeys, useContentDetail } from '@/hooks/useContentQueries';

type DraftSendReadyItem = Extract<ConversationItem, { kind: 'draft_send_ready' }>;

export interface MessageDraftSendDialogProps {
  open: boolean;
  item: DraftSendReadyItem | null;
  onClose: () => void;
}

export function MessageDraftSendDialog({ open, item, onClose }: MessageDraftSendDialogProps) {
  const queryClient = useQueryClient();
  const detailQuery = useContentDetail(item?.draftContentId ?? 0, {
    enabled: open && item !== null,
  });
  const detail = detailQuery.data;
  const alreadySent =
    detail !== undefined && detail.contentType === 'MESSAGE'
      ? detail.isDraft === false || detail.sentAt !== null
      : false;

  const sendMutation = useMutation({
    mutationFn: (draftContentId: number) => sendMessageDraft(draftContentId),
    onSuccess: async () => {
      toast.success('Reply sent');
      if (item) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: contentKeys.detail(item.draftContentId) }),
          ...(item.sourceMessageContentId !== null
            ? [queryClient.invalidateQueries({ queryKey: contentKeys.detail(item.sourceMessageContentId) })]
            : []),
          queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
          queryClient.invalidateQueries({
            predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'content-ref',
          }),
        ]);
      }
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to send the draft.'));
    },
  });

  if (!item) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send reply?</DialogTitle>
          <DialogDescription>Review the draft and confirm sending from your account.</DialogDescription>
        </DialogHeader>
        <DialogBody className="min-w-0 space-y-2 text-sm">
          <p className="min-w-0 truncate font-medium">{item.subject ?? '(no subject)'}</p>
          <p className="min-w-0 truncate text-muted-foreground">To {item.toSummary}</p>
          <p className="min-w-0 truncate text-muted-foreground">From {item.sendingAccountEmail ?? '—'}</p>
          {item.authoredPreview ? (
            <p className="min-w-0 truncate text-xs text-muted-foreground">{item.authoredPreview}</p>
          ) : null}
        </DialogBody>
        <DialogFooter className="gap-2 sm:flex-col">
          {alreadySent ? (
            <p className="w-full text-center text-sm font-medium text-muted-foreground">Sent</p>
          ) : (
            <Button
              type="button"
              size="lg"
              className="h-12 w-full text-base"
              disabled={sendMutation.isPending}
              onClick={() => sendMutation.mutate(item.draftContentId)}
            >
              {sendMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send
            </Button>
          )}
          <Button type="button" variant="outline" className="w-full" onClick={onClose}>
            Review on page
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
